import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { Crosshair } from "@/components/Crosshair";
import {
  RefreshCw, Loader2, ScrollText, ShieldAlert, Package, DollarSign, UserPlus,
  MessageSquarePlus, Briefcase, Receipt, Layers, Award, User, Search, X,
} from "lucide-react";
import { DiscussButton } from "@/components/admin/DiscussButton";

const ACTIVITY_META = {
  listings: { accent: "border-l-olive-bright", icon: Package, tag: "listing" },
  sales: { accent: "border-l-volt", icon: DollarSign, tag: "sale" },
  users: { accent: "border-l-teal-400", icon: UserPlus, tag: "signup" },
  reports: { accent: "border-l-blaze", icon: ShieldAlert, tag: "report" },
  feedback: { accent: "border-l-[#E8C46A]", icon: MessageSquarePlus, tag: "feedback" },
  dealers: { accent: "border-l-amber-400", icon: Briefcase, tag: "dealer" },
};

const AUDIT_META = {
  "config.fees": { accent: "border-l-teal-400", icon: Receipt },
  "config.tiers": { accent: "border-l-teal-400", icon: Layers },
  "moderation.action": { accent: "border-l-blaze", icon: ShieldAlert },
  "dealer.approve": { accent: "border-l-amber-400", icon: Briefcase },
  "dealer.reject": { accent: "border-l-amber-400", icon: Briefcase },
  "user.badges": { accent: "border-l-olive-bright", icon: Award },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "audit", label: "Admin actions" },
  { id: "activity", label: "Platform events" },
];

function EventRow({ ev }) {
  const meta = ev.kind === "audit"
    ? (AUDIT_META[ev.action] || { accent: "border-l-olive-bright", icon: User })
    : (ACTIVITY_META[ev.source] || { accent: "border-l-sniper", icon: ScrollText });
  const Icon = meta.icon;
  return (
    <li className={`pl-4 pr-4 py-3 border-l-2 ${meta.accent}`} data-testid={`audit-row-${ev.id}`}>
      <div className="flex items-start gap-2.5">
        <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" strokeWidth={1.7} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-body text-foreground">{ev.summary}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {ev.kind === "audit" && (
              <span className="text-[10px] font-mono uppercase text-olive-bright" data-testid="audit-actor">
                {ev.actor || "admin"}
              </span>
            )}
            <span className="text-[10px] font-mono uppercase text-muted-foreground">{timeAgo(ev.occurred_at)}</span>
            <span className="text-[10px] font-mono uppercase text-muted-foreground/60">· {ev.kind === "audit" ? ev.action : ev.source}</span>
          </div>
        </div>
        {ev.kind === "audit" && <DiscussButton type="audit_event" id={ev.id} label="Discuss" compact />}
      </div>
    </li>
  );
}

export default function AdminAuditEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(60);
  const [actionType, setActionType] = useState("all");
  const [actions, setActions] = useState([]);
  const [queryInput, setQueryInput] = useState("");
  const [activeQuery, setActiveQuery] = useState("");

  const load = useCallback(async (lim, actType, term) => {
    try {
      const p = new URLSearchParams({ limit: String(lim) });
      if (actType && actType !== "all") p.set("action", actType);
      if (term) p.set("q", term);
      // A type or username filter scopes the stream to admin actions (platform events
      // have no action/actor to match), so we skip the activity feed while one is active.
      const scoped = (actType && actType !== "all") || !!term;
      const reqs = [api.get(`/admin/audit-events?${p.toString()}`)];
      if (!scoped) reqs.push(api.get(`/admin/activity?limit=${lim}`));  // test data always excluded
      const [audit, activity] = await Promise.all(reqs);
      if (Array.isArray(audit.data.actions)) setActions(audit.data.actions);
      const auditEv = (audit.data.items || []).map((e) => ({
        id: e.event_id, kind: "audit", action: e.action, actor: e.actor_username,
        summary: e.summary, occurred_at: e.created_at,
      }));
      const activityEv = activity ? (activity.data.items || []).map((e) => ({
        id: e.id, kind: "activity", source: e.source, summary: e.summary, occurred_at: e.occurred_at,
      })) : [];
      const merged = [...auditEv, ...activityEv].sort(
        (a, b) => (b.occurred_at || "").localeCompare(a.occurred_at || "")
      );
      setEvents(merged);
    } catch { /* keep prior */ }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    load(limit, actionType, activeQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, actionType, activeQuery]);

  // Debounce the username search input into activeQuery.
  useEffect(() => {
    const t = setTimeout(() => setActiveQuery(queryInput.trim()), 350);
    return () => clearTimeout(t);
  }, [queryInput]);

  const refresh = () => { setRefreshing(true); load(limit, actionType, activeQuery); };

  const scoped = actionType !== "all" || !!activeQuery;
  const shown = events.filter((e) => filter === "all" || e.kind === filter);

  return (
    <div className="text-foreground" data-testid="admin-audit-events">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="audit-events-title">Audit Events</h2>
          <p className="text-xs font-mono uppercase text-muted-foreground mt-1">Full chronological stream — admin actions &amp; platform events</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} data-testid="audit-refresh" className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-foreground font-mono uppercase text-sm px-4 py-2.5 transition-colors">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <Crosshair className="my-6" />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="audit-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`audit-filter-${f.id}`}
            data-active={filter === f.id || undefined}
            disabled={scoped && f.id === "activity"}
            className={`font-mono uppercase text-xs px-3 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${filter === f.id ? "border-olive-bright bg-olive/20 text-foreground" : "border-sniper text-secondary-foreground hover:text-foreground hover:border-olive"}`}
          >
            {f.label}
          </button>
        ))}

        <div className="w-px h-6 bg-sniper mx-1 hidden sm:block" />

        {/* Event-type filter — options are the actions actually emitted (from the backend distinct list). */}
        <select
          value={actionType}
          onChange={(e) => setActionType(e.target.value)}
          data-testid="audit-type-filter"
          className="bg-input border border-sniper focus:border-olive outline-none font-mono uppercase text-xs px-3 py-2 text-foreground"
          title="Filter by event type"
        >
          <option value="all">All types</option>
          {actions.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        {/* Username search — matches actor OR subject (subject handles live in the summary). */}
        <div className="relative flex-1 min-w-[180px] max-w-[280px]">
          <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            data-testid="audit-username-search"
            placeholder="Search @username (actor or subject)"
            className="w-full bg-input border border-sniper focus:border-olive outline-none font-mono text-xs pl-8 pr-8 py-2 text-foreground placeholder:text-muted-foreground/70"
          />
          {queryInput && (
            <button onClick={() => setQueryInput("")} data-testid="audit-username-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {scoped && (
          <button onClick={() => { setActionType("all"); setQueryInput(""); }} data-testid="audit-filters-clear"
            className="font-mono uppercase text-[10px] px-2.5 py-2 border border-sniper text-muted-foreground hover:text-foreground hover:border-olive transition-colors">
            Clear filters
          </button>
        )}
      </div>

      {scoped && (
        <p className="text-[10px] font-mono uppercase text-muted-foreground -mt-2 mb-4" data-testid="audit-scoped-note">
          Showing admin actions only{actionType !== "all" ? ` · type: ${actionType}` : ""}{activeQuery ? ` · matching "${activeQuery}"` : ""}
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-secondary-foreground font-mono text-sm" data-testid="audit-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading events…
        </div>
      ) : (
        <div className="border border-sniper bg-surface" data-testid="audit-stream">
          {shown.length === 0 ? (
            <p className="text-sm font-body text-muted-foreground p-6" data-testid="audit-empty">No events yet.</p>
          ) : (
            <ul className="divide-y divide-sniper" data-testid="audit-list">
              {shown.map((ev) => <EventRow key={`${ev.kind}-${ev.id}`} ev={ev} />)}
            </ul>
          )}
        </div>
      )}

      {!loading && events.length >= limit && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => setLimit((l) => l + 60)}
            data-testid="audit-load-more"
            className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-5 py-2.5 transition-colors"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
